import {useContext, useMemo, useState} from "react";
import {ActionIcon, Box, SegmentedControl, Table, Text} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {GPTMetaContext} from "../MetaProvider";
import {resolvesToProffSources, SERIES_COLORS} from "./ChartBlock";
import {metricLabel} from "./metricLabel";
import {unitPhrase} from "./unitPhrase";
import classes from "./CompareBlock.module.css";

export type CompareGap = {
    value: number | null;
    display: string | null;
};

export type CompareItem = {
    // Stable company identity for comparisons that span years. Legacy/category
    // items continue to use their display label as identity.
    companyId?: string;
    label: string;
    value: number | null;
    display: string | null;
    gapToLeader?: CompareGap;
};

export type CompareDifference = {
    display: string | null;
    between?: string[];
    ratioDisplay?: string;
};

export type CompareRow = {
    // Describes the identity represented by item labels. Missing values keep
    // the legacy company/category color behavior.
    itemDimension?: "year" | "company";
    // A backend-identified metric resolves to its label via metricLabel and
    // takes priority over label. label is then optional; it stays required
    // behavior for backward compatible content with no metric.
    metric?: string;
    label?: string;
    unit?: string;
    // Shared across every year page for this metric so paging does not rescale bars.
    valueDomain?: {min: number; max: number};
    items: CompareItem[];
    difference?: CompareDifference;
};

export type CompareYearPage = {
    year: number;
    rows: CompareRow[];
};

export type CompareNode = {
    type: "compare";
    title: string;
    unit: string;
    source?: number | number[] | string | string[];
    rows: CompareRow[];
    // Multi-company, multi-year compares. Root rows remain the default-year
    // fallback for clients that do not yet understand pages.
    yearPages?: CompareYearPage[];
    defaultYear?: number;
    displayTitle?: string;
    subtitle?: string;
    note?: string;
    defaultView?: "visual" | "table";
};

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const isNonEmptyString = (value: unknown): value is string =>
    typeof value === "string" && value.length > 0;

const isCompareGap = (value: unknown): value is CompareGap => {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const candidate = value as CompareGap;
    if (!(isFiniteNumber(candidate.value) || candidate.value === null)) {
        return false;
    }
    return isNonEmptyString(candidate.display) || candidate.display === null;
};

const isCompareValueDomain = (value: unknown): value is {min: number; max: number} => {
    if (typeof value !== "object" || value === null) return false;
    const candidate = value as {min?: unknown; max?: unknown};
    return isFiniteNumber(candidate.min) && isFiniteNumber(candidate.max) &&
        candidate.min <= 0 && candidate.max >= 0 && candidate.min <= candidate.max;
};

/** An item is valid when it has a filled value and display, or when both are
 *  explicitly absent (the company has no figure for that year). One field
 *  filled and the other missing is invalid. gapToLeader is optional: absent
 *  on older content, and absent on the row's own leader item. */
const isCompareItem = (value: unknown): value is CompareItem => {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const candidate = value as CompareItem;
    if (!isNonEmptyString(candidate.label)) {
        return false;
    }
    if (candidate.companyId !== undefined && !isNonEmptyString(candidate.companyId)) {
        return false;
    }
    if (candidate.gapToLeader !== undefined && !isCompareGap(candidate.gapToLeader)) {
        return false;
    }
    if (isFiniteNumber(candidate.value) && isNonEmptyString(candidate.display)) {
        return true;
    }
    return candidate.value === null && (candidate.display === null || candidate.display === undefined);
};

const isCompareDifference = (value: unknown): value is CompareDifference => {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const candidate = value as CompareDifference;
    if (!isNonEmptyString(candidate.display) && candidate.display !== null) {
        return false;
    }
    if (candidate.between !== undefined) {
        if (!Array.isArray(candidate.between) || !candidate.between.every((v) => typeof v === "string")) {
            return false;
        }
    }
    if (candidate.ratioDisplay !== undefined && typeof candidate.ratioDisplay !== "string") {
        return false;
    }
    return true;
};

const isCompareRow = (value: unknown): value is CompareRow => {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const candidate = value as CompareRow;
    // A row needs a label, a metric id, or both: it must resolve to some
    // heading one way or the other.
    if (!isNonEmptyString(candidate.label) && !isNonEmptyString(candidate.metric)) {
        return false;
    }
    if (candidate.label !== undefined && !isNonEmptyString(candidate.label)) {
        return false;
    }
    if (candidate.metric !== undefined && !isNonEmptyString(candidate.metric)) {
        return false;
    }
    if (candidate.unit !== undefined && typeof candidate.unit !== "string") {
        return false;
    }
    if (candidate.valueDomain !== undefined && !isCompareValueDomain(candidate.valueDomain)) {
        return false;
    }
    if (
        candidate.itemDimension !== undefined &&
        candidate.itemDimension !== "year" &&
        candidate.itemDimension !== "company"
    ) {
        return false;
    }
    if (!Array.isArray(candidate.items) || candidate.items.length === 0) {
        return false;
    }
    if (!candidate.items.every(isCompareItem)) {
        return false;
    }
    const companyIds = candidate.items.map((item) => item.companyId).filter(isNonEmptyString);
    if (new Set(companyIds).size !== companyIds.length) {
        return false;
    }
    if (
        candidate.valueDomain !== undefined &&
        candidate.items.some((item) => isFiniteNumber(item.value) &&
            (item.value < candidate.valueDomain!.min || item.value > candidate.valueDomain!.max))
    ) {
        return false;
    }
    if (candidate.difference !== undefined && !isCompareDifference(candidate.difference)) {
        return false;
    }
    return true;
};

const isCompareYearPage = (value: unknown): value is CompareYearPage => {
    if (typeof value !== "object" || value === null) return false;
    const candidate = value as CompareYearPage;
    return Number.isInteger(candidate.year) && Array.isArray(candidate.rows) &&
        candidate.rows.length > 0 && candidate.rows.every(isCompareRow);
};

const isNumberArray = (value: unknown): value is number[] =>
    Array.isArray(value) && value.length > 0 && value.every(isFiniteNumber);

export type IsCompareNodeOptions = {
    // Set when the fence's own language is "compare": the language already
    // decides this is a compare block, so a "type" field is optional.
    ignoreType?: boolean;
};

/**
 * Parses a code block's raw text as a compare node. Returns null for anything
 * that is not valid JSON, not marked expanded, or missing a usable row, so a
 * partially streamed block never renders as broken.
 */
export function isCompareNode(text: unknown, options: IsCompareNodeOptions = {}): CompareNode | null {
    if (typeof text !== "string") {
        return null;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return null;
    }

    if (typeof parsed !== "object" || parsed === null) {
        return null;
    }

    const candidate = parsed as Record<string, unknown>;
    if (!options.ignoreType && candidate.type !== "compare") {
        return null;
    }
    if (candidate.expanded !== true) {
        return null;
    }
    if (!Array.isArray(candidate.rows) || candidate.rows.length === 0) {
        return null;
    }
    if (!candidate.rows.every(isCompareRow)) {
        return null;
    }

    const source = isFiniteNumber(candidate.source)
        ? candidate.source
        : isNumberArray(candidate.source)
          ? candidate.source
          : typeof candidate.source === "string"
            ? candidate.source
            : Array.isArray(candidate.source) && candidate.source.every((v: unknown) => typeof v === "string")
              ? (candidate.source as string[])
          : undefined;

    let yearPages: CompareYearPage[] | undefined;
    if (
        Array.isArray(candidate.yearPages) &&
        candidate.yearPages.length > 0 &&
        candidate.yearPages.every(isCompareYearPage) &&
        new Set(candidate.yearPages.map((page) => page.year)).size === candidate.yearPages.length
    ) {
        yearPages = [...candidate.yearPages].sort((a, b) => a.year - b.year);
    }

    const defaultYear = isFiniteNumber(candidate.defaultYear) && Number.isInteger(candidate.defaultYear) &&
        yearPages?.some((page) => page.year === candidate.defaultYear)
        ? candidate.defaultYear
        : undefined;

    return {
        type: "compare",
        title: typeof candidate.title === "string" ? candidate.title : "",
        unit: typeof candidate.unit === "string" ? candidate.unit : "",
        source,
        rows: candidate.rows,
        ...(yearPages ? {yearPages} : {}),
        ...(defaultYear !== undefined ? {defaultYear} : {}),
        ...(typeof candidate.displayTitle === "string" && candidate.displayTitle.trim() ? {displayTitle: candidate.displayTitle} : {}),
        ...(typeof candidate.subtitle === "string" && candidate.subtitle.trim() ? {subtitle: candidate.subtitle} : {}),
        ...(typeof candidate.note === "string" && candidate.note.trim() ? {note: candidate.note} : {}),
        ...(candidate.defaultView === "visual" || candidate.defaultView === "table" ? {defaultView: candidate.defaultView} : {}),
    };
}

export const looksLikeCompareFence = (lang: string | null): boolean => lang === "compare";

const TYPE_FIELD_OPEN = /^\{\s*"type"\s*:\s*"/;
export const looksLikeCompareJson = (trimmed: string): boolean => {
    const opened = TYPE_FIELD_OPEN.exec(trimmed);
    if (!opened) return false;
    const rest = trimmed.slice(opened[0].length);
    const closingQuote = rest.indexOf('"');
    return closingQuote === -1 ? "compare".startsWith(rest) : rest.slice(0, closingQuote) === "compare";
};

/** Assigns each distinct company/category label the next fixed color slot,
 *  in the order labels first appear. Year labels use one series color instead. */
const itemIdentity = (item: CompareItem) => item.companyId ?? item.label;

const buildColorByIdentity = (rows: CompareRow[]): Map<string, string> => {
    const colorByIdentity = new Map<string, string>();
    let nextSlot = 0;
    for (const row of rows) {
        if (row.itemDimension === "year") {
            continue;
        }
        for (const item of row.items) {
            const identity = itemIdentity(item);
            if (!colorByIdentity.has(identity)) {
                colorByIdentity.set(identity, SERIES_COLORS[nextSlot % SERIES_COLORS.length]);
                nextSlot += 1;
            }
        }
    }
    return colorByIdentity;
};

/** The zero baseline and per-item bar geometry for one row, scaled to that
 *  row's own values only. Negative values extend left of the zero line
 *  instead of overflowing the track. An item with no value gets no bar,
 *  and is left out of the scale so it cannot flatten the other bars. */
const rowGeometry = (items: CompareItem[], valueDomain?: CompareRow["valueDomain"]) => {
    const values = items.map((i) => i.value).filter(isFiniteNumber);
    const min = valueDomain?.min ?? Math.min(0, ...values);
    const max = valueDomain?.max ?? Math.max(0, ...values);
    const range = max - min || 1;
    const valueToPercent = (value: number) => ((value - min) / range) * 100;
    const zeroPercent = valueToPercent(0);
    const bars = items.map((item) => {
        if (!isFiniteNumber(item.value)) {
            return null;
        }
        const valuePercent = valueToPercent(item.value);
        return {
            left: Math.min(zeroPercent, valuePercent),
            width: Math.abs(valuePercent - zeroPercent),
        };
    });
    return {zeroPercent, bars, valueToPercent};
};

type CompareBlockProps = {
    compare: CompareNode;
};

export const CompareBlock = ({compare}: CompareBlockProps) => {
    const {t} = useTranslation();
    const meta = useContext(GPTMetaContext);
    const identity = JSON.stringify(compare);
    const yearPages = useMemo(() => compare.yearPages ?? [], [compare.yearPages]);
    const defaultYear = useMemo(() => {
        if (compare.defaultYear !== undefined && yearPages.some((page) => page.year === compare.defaultYear)) {
            return compare.defaultYear;
        }
        const pagesWithValues = yearPages.filter((page) =>
            page.rows.some((row) => row.items.some((item) => isFiniteNumber(item.value)))
        );
        const latestPage = pagesWithValues[pagesWithValues.length - 1] ?? yearPages[yearPages.length - 1];
        return latestPage?.year;
    }, [compare.defaultYear, yearPages]);
    const [selection, setSelection] = useState<{
        identity: string;
        view: "visual" | "table";
        year?: number;
    }>({
        identity,
        view: compare.defaultView ?? "visual",
        year: defaultYear,
    });

    const selectedYear = selection.identity === identity &&
        yearPages.some((page) => page.year === selection.year)
        ? selection.year
        : defaultYear;
    const selectedPage = yearPages.find((page) => page.year === selectedYear);
    const rows = selectedPage?.rows ?? compare.rows;
    const pageIndex = yearPages.findIndex((page) => page.year === selectedYear);
    const previousPage = pageIndex > 0 ? yearPages[pageIndex - 1] : undefined;
    const nextPage = pageIndex >= 0 && pageIndex < yearPages.length - 1 ? yearPages[pageIndex + 1] : undefined;
    const showYearPager = yearPages.length > 1 && selectedYear !== undefined;

    const stableRows = useMemo(() => {
        const defaultRows = yearPages.find((page) => page.year === defaultYear)?.rows ?? compare.rows;
        return [
            ...defaultRows,
            ...yearPages.filter((page) => page.year !== defaultYear).flatMap((page) => page.rows),
            ...compare.rows,
        ];
    }, [compare.rows, defaultYear, yearPages]);
    const colorByIdentity = useMemo(() => buildColorByIdentity(stableRows), [stableRows]);
    const companyItems = useMemo(() => {
        const items = new Map<string, CompareItem>();
        for (const row of stableRows) {
            for (const item of row.items) {
                const key = itemIdentity(item);
                if (!items.has(key)) items.set(key, item);
            }
        }
        return [...items.entries()].map(([key, item]) => ({key, label: item.label, originalLabel: item.label}));
    }, [stableRows]);
    const companyOrder = useMemo(
        () => new Map(companyItems.map(({key}, index) => [key, index] as const)),
        [companyItems]
    );
    const hasMeaningfulComparison = useMemo(
        () => rows.some((row) =>
            new Set(row.items.filter((item) => isFiniteNumber(item.value)).map(itemIdentity)).size >= 2
        ),
        [rows]
    );
    const view = !hasMeaningfulComparison
        ? "table"
        : selection.identity === identity
          ? selection.view
          : compare.defaultView ?? "visual";

    const showProffAttribution = useMemo(
        () => resolvesToProffSources(compare.source, meta?.rawSources ?? meta?.sources),
        [meta?.rawSources, meta?.sources, compare.source]
    );

    const rowHeadings = useMemo(
        () => rows.map((row) => (row.metric ? metricLabel(row.metric, t) : (row.label ?? ""))),
        [rows, t]
    );

    // A title equal to the only row's heading would repeat that row's own
    // caption, so it is dropped rather than shown twice.
    const showTitle =
        (compare.displayTitle ?? compare.title) && !(rows.length === 1 && (compare.displayTitle ?? compare.title) === rowHeadings[0]);

    const updateSelection = (next: Partial<Pick<typeof selection, "view" | "year">>) => {
        setSelection((current) => ({
            identity,
            view: current.identity === identity ? current.view : compare.defaultView ?? "visual",
            year: current.identity === identity ? current.year : defaultYear,
            ...next,
        }));
    };

    return (
        <Box className={classes.container}>
            {showTitle && (
                <Text fw={600} className={classes.title} title={compare.displayTitle && compare.displayTitle !== compare.title ? compare.title : undefined}>
                    {compare.displayTitle ?? compare.title}
                </Text>
            )}
            {compare.subtitle && <Text size="sm" c="dimmed">{compare.subtitle}</Text>}
            {(hasMeaningfulComparison || showYearPager) && (
                <Box className={`${classes.controls} ${hasMeaningfulComparison ? classes.viewControl : ""}`}>
                    {showYearPager && (
                        <Box className={classes.yearPager}>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                disabled={!previousPage}
                                aria-label={t("compare.year.previousPage", {defaultValue: "Previous year"})}
                                onClick={() => previousPage && updateSelection({year: previousPage.year})}
                            >
                                ‹
                            </ActionIcon>
                            <Text component="span" size="sm" fw={600} aria-live="polite">
                                {selectedYear}
                            </Text>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                disabled={!nextPage}
                                aria-label={t("compare.year.nextPage", {defaultValue: "Next year"})}
                                onClick={() => nextPage && updateSelection({year: nextPage.year})}
                            >
                                ›
                            </ActionIcon>
                        </Box>
                    )}
                    {hasMeaningfulComparison && (
                    <SegmentedControl
                        size="xs"
                        aria-label={t("compare.view.switch", {defaultValue: "Comparison view"})}
                        value={view}
                        onChange={(nextView) => updateSelection({view: nextView as "visual" | "table"})}
                        data={[
                            {value: "visual", label: t("compare.view.visual", {defaultValue: "Comparison"})},
                            {value: "table", label: t("compare.view.table", {defaultValue: "Table"})},
                        ]}
                    />
                    )}
                </Box>
            )}
            {view === "table" ? (
                <Box className={classes.tableScroll}>
                    <Table striped withTableBorder>
                        <Table.Thead>
                            {companyItems.length === 1 ? (
                                <Table.Tr>
                                    <Table.Th
                                        scope="colgroup"
                                        colSpan={2}
                                        className={classes.singleCompanyHeading}
                                        title={companyItems[0].label !== companyItems[0].originalLabel ? companyItems[0].originalLabel : undefined}
                                    >
                                        {companyItems[0].label}
                                    </Table.Th>
                                </Table.Tr>
                            ) : (
                                <Table.Tr>
                                    <Table.Th scope="col">
                                        {t("compare.view.metric", {defaultValue: "Metric"})}
                                    </Table.Th>
                                    {companyItems.map(({key, label, originalLabel}) => (
                                        <Table.Th key={key} scope="col" className={classes.numericCell} title={label !== originalLabel ? originalLabel : undefined}>
                                            {label}
                                        </Table.Th>
                                    ))}
                                </Table.Tr>
                            )}
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.map((row, rowIndex) => {
                                const unit = row.unit || compare.unit;
                                const heading = [
                                    rowHeadings[rowIndex],
                                    unit ? unitPhrase(unit, t) : "",
                                ].filter(Boolean).join(" ");
                                const itemByIdentity = new Map(row.items.map((item) => [itemIdentity(item), item]));
                                return (
                                    <Table.Tr key={`${row.metric ?? row.label}-${rowIndex}`}>
                                        <Table.Th scope="row">{heading}</Table.Th>
                                        {companyItems.map(({key}) => (
                                            <Table.Td key={key} className={classes.numericCell}>
                                                {itemByIdentity.get(key)?.display ?? "—"}
                                            </Table.Td>
                                        ))}
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                </Box>
            ) : (
            <>
            {rows.map((row, rowIndex) => {
                const items = [...row.items].sort(
                    (a, b) =>
                        (companyOrder.get(itemIdentity(a)) ?? Infinity) -
                        (companyOrder.get(itemIdentity(b)) ?? Infinity)
                );
                const hasRowComparison =
                    new Set(items.filter((item) => isFiniteNumber(item.value)).map(itemIdentity)).size >= 2;
                const {zeroPercent, bars} = rowGeometry(items, row.valueDomain);
                const hasNegativeValue =
                    hasRowComparison && items.some((item) => isFiniteNumber(item.value) && item.value < 0);
                const hasGapContract =
                    hasRowComparison && items.some((item) => item.gapToLeader !== undefined);

                return (
                    <Box key={`${row.metric ?? row.label}-${rowIndex}`} className={classes.row}>
                        <Text size="sm" c="dimmed" className={classes.rowLabel}>
                            ({rowHeadings[rowIndex]})
                            {hasGapContract && (
                                <span className={classes.gapCaption}>
                                    {` · ${t("compare.differenceFromHighest", {defaultValue: "Difference from highest"})}`}
                                </span>
                            )}
                        </Text>
                        <ul
                            className={`${classes.items} ${hasRowComparison ? "" : classes.itemsWithoutComparison}`}
                        >
                            {items.map((item, itemIndex) => {
                                const geometry = bars[itemIndex];
                                const valueText = item.display ?? t("compare.noValue");
                                const deltaText = hasRowComparison && item.gapToLeader?.display
                                    ? `−${item.gapToLeader.display}`
                                    : "";
                                const baselineText =
                                    hasRowComparison && hasGapContract && isFiniteNumber(item.value) && item.gapToLeader === undefined
                                        ? `, ${t("compare.differenceFromHighest", {defaultValue: "Difference from highest"})} 0`
                                        : "";
                                const ariaLabel = deltaText
                                    ? `${item.label}: ${valueText}, ${t("compare.differenceFromHighest", {defaultValue: "Difference from highest"})} ${deltaText}`
                                    : `${item.label}: ${valueText}${baselineText}`;

                                return (
                                    <li
                                        key={`${item.label}-${itemIndex}`}
                                        className={classes.item}
                                        aria-label={ariaLabel}
                                    >
                                        <span className={classes.itemLabel} aria-hidden="true">
                                            {item.label}
                                        </span>
                                        <div
                                            className={hasRowComparison ? classes.track : classes.emptyTrack}
                                            aria-hidden="true"
                                        >
                                            {hasRowComparison && hasNegativeValue && (
                                                <span
                                                    className={classes.zeroLine}
                                                    style={{left: `${zeroPercent}%`}}
                                                />
                                            )}
                                            {hasRowComparison && geometry && item.value !== 0 && (
                                                <span
                                                    className={classes.bar}
                                                    style={{
                                                        left: `${geometry.left}%`,
                                                        width: `${geometry.width}%`,
                                                        backgroundColor:
                                                            row.itemDimension === "year"
                                                                ? SERIES_COLORS[0]
                                                            : colorByIdentity.get(itemIdentity(item)),
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <span className={classes.valueCell} aria-hidden="true">
                                            <span
                                                className={
                                                    item.display === null
                                                        ? `${classes.itemValue} ${classes.itemValueMuted}`
                                                        : classes.itemValue
                                                }
                                            >
                                                {valueText}
                                            </span>
                                        </span>
                                        {hasRowComparison && (
                                            <span className={classes.itemGap} aria-hidden="true">
                                                {deltaText}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </Box>
                );
            })}
            </>
            )}
            {showProffAttribution && (
                <Text size="xs" c="dimmed" className={classes.footnote}>
                    {t("source.companyLookup")}
                </Text>
            )}
            {compare.note && <Text size="xs" c="dimmed" className={classes.footnote}>{compare.note}</Text>}
        </Box>
    );
};

/**
 * Placeholder shown while a compare block's JSON is still streaming in.
 * Shorter than the chart skeleton: a compare block has no axis or legend.
 */
export const CompareSkeleton = () => {
    const {t} = useTranslation();
    return (
        <Box
            className={classes.skeleton}
            aria-busy="true"
            aria-label={t("compare.loading")}
        />
    );
};

export const COMPARE_UNAVAILABLE_DEFAULT = "Sammenligningen kunne ikke vises.";

export const CompareUnavailable = () => {
    const {t} = useTranslation();
    return <Text size="sm" c="dimmed" className={classes.footnote}>{t("compare.unavailable", {defaultValue: COMPARE_UNAVAILABLE_DEFAULT})}</Text>;
};
